import { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query, limit as fsLimit } from 'firebase/firestore';
import {
  DailyHealthSchema,
  type DailyHealth,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/health/useHealth';

export type HealthState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; health: DailyHealth | null };

/**
 * Lê o último documento em /aggregations/health/days/. Mostra o
 * snapshot mais recente — health diário; caller pode no futuro
 * estender para histórico de signals.
 *
 * Origem: sem issue — commit 030a61a79, 2026-05-04 (rota /dashboard/health).
 *
 * Contrato: sem entrada → `HealthState`, leitura one-shot ao montar. Coleção
 * vazia ou doc fora do `DailyHealthSchema` → `READY` com `health: null` (este
 * só com `console.warn`); erro de leitura → `ERROR` só com `console.error`.
 * Nenhum dos dois caminhos reporta ao PostHog (achado registrado na #926).
 *
 * Usado por: `HealthDashboardPage.tsx` (rota `/dashboard/health`; web
 * `apps/admin`, super-admin).
 *
 * Arquitetura: só leitura de coleção direta via
 * `match /aggregations/{type}/{subcoll}/{id}` de `firebase/firestore.rules`
 * (`isSuperAdmin()`); `orderBy` num campo só usa o índice automático.
 * Escritor: o scheduler `aggregateHealth` (Functions). O mesmo doc alimenta
 * as abas churn-risk/zombie de `useCustomers`.
 *
 * Dados (anonimizado; `DailyHealthSchema` em
 * `packages/types/src/aggregations.ts`):
 *   aggregations/health/days/{yyyymmdd}
 *     { "yyyymmdd": "20261007", "schemaVersion": 1,
 *       "countsBySignal": { "ZOMBIE": 3, "PRE_CHURN_RISK": 1 },
 *       "topProblematic": [{ "tenantId": "tenant-1", "signals": ["ZOMBIE"] }], … }
 */
export function useHealth(): HealthState {
  const [state, setState] = useState<HealthState>({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });
    async function load(): Promise<void> {
      try {
        const ref = collection(firestore, 'aggregations', 'health', 'days');
        const q = query(ref, orderBy('yyyymmdd', 'desc'), fsLimit(1));
        const snap = await getDocs(q);
        if (guard.cancelled) return;
        const doc = snap.docs[0];
        if (!doc) {
          setState({ kind: 'READY', health: null });
          return;
        }
        const parsed = DailyHealthSchema.safeParse(doc.data());
        if (!parsed.success) {
          console.warn(`${TAG}: ${doc.id} ignorado`, parsed.error.issues);
          setState({ kind: 'READY', health: null });
          return;
        }
        setState({ kind: 'READY', health: parsed.data });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_health_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, []);

  return state;
}
